import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { apiClient } from '../../../shared/api/axios-instance'
import { message } from 'antd'

export interface NotificationTemplate {
    id: number;
    event_name: string;
    channel: 'sms' | 'email' | 'whatsapp' | 'push';
    language: string;
    subject: string | null;
    body: string;
    provider: string | null;
    provider_template_id: string | null;
    version: number;
    is_active: boolean;
    created_at: string;
    updated_at: string;
}

export const useNotificationTemplates = () => {
    const queryClient = useQueryClient()

    // Fetch templates
    const templatesQuery = useQuery({
        queryKey: ['notification-templates'],
        queryFn: async () => {
            const response = await apiClient.get<NotificationTemplate[]>('/admin/templates')
            return response.data
        }
    })

    // Create template
    const createTemplateMutation = useMutation({
        mutationFn: async (data: Partial<NotificationTemplate>) => {
            const response = await apiClient.post<NotificationTemplate>('/admin/templates', data)
            return response.data
        },
        onSuccess: () => {
            message.success('Template created successfully')
            queryClient.invalidateQueries({ queryKey: ['notification-templates'] })
        }
    })

    // Update template
    const updateTemplateMutation = useMutation({
        mutationFn: async ({ id, data }: { id: number, data: Partial<NotificationTemplate> }) => {
            const response = await apiClient.put<NotificationTemplate>(`/admin/templates/${id}`, data)
            return response.data
        },
        onSuccess: () => {
            message.success('Template updated successfully')
            queryClient.invalidateQueries({ queryKey: ['notification-templates'] })
        }
    })

    // Delete template
    const deleteTemplateMutation = useMutation({
        mutationFn: async (id: number) => {
            await apiClient.delete(`/admin/templates/${id}`)
        },
        onSuccess: () => {
            message.success('Template deleted successfully')
            queryClient.invalidateQueries({ queryKey: ['notification-templates'] })
        }
    })

    return {
        templates: templatesQuery.data || [],
        isLoading: templatesQuery.isLoading,
        error: templatesQuery.error,
        createTemplate: createTemplateMutation.mutateAsync,
        updateTemplate: updateTemplateMutation.mutateAsync,
        deleteTemplate: deleteTemplateMutation.mutateAsync,
        isCreating: createTemplateMutation.isPending,
        isUpdating: updateTemplateMutation.isPending,
        isDeleting: deleteTemplateMutation.isPending,
    }
}
